// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: GitHub Actions runs in Agents (Runs, JP 2026-11-02, design/pr-tool 9-11): what runs, failed
// or finished in the person's repositories, read by the API with their own GitHub sign-in
// (GET /konvo/agents/runs), and one run's jobs (POST /konvo/agents/runs/detail). The grouping and
// filters are pure functions so AgentRuns.test.ts covers them.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import type {AgentRun, AgentRunDetail, AgentRunRef, AgentRuns} from '@konvo/schema/src/domains/konvo/Agents';
import {makeAutoObservable, observable, runInAction} from 'mobx';

export const runKey = (ref: AgentRunRef) => `${ref.repository.toLowerCase()}#${ref.runId}`;

export type RunFilter = 'all' | 'running' | 'failed' | 'releases';

export interface RunGroups {
	running: Array<AgentRun>;
	failed: Array<AgentRun>;
	recent: Array<AgentRun>;
}

/** A run someone started by hand (a release, a deploy): GitHub's workflow_dispatch. */
export const isRelease = (run: Pick<AgentRun, 'event'>) => run.event === 'workflow_dispatch';

/**
 * Running now (and waiting for a runner), failed (the latest run of each workflow and branch only:
 * one that passed again since isn't news), and the rest, newest first.
 */
export function runGroups(runs: ReadonlyArray<AgentRun>, filter: RunFilter, repository: string | null): RunGroups {
	const repo = repository?.toLowerCase() ?? null;
	const groups: RunGroups = {running: [], failed: [], recent: []};
	const newestFirst = [...runs].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
	const seen = new Set<string>();
	for (const run of newestFirst) {
		if (repo && run.repository.toLowerCase() !== repo) continue;
		if (filter === 'releases' && !isRelease(run)) continue;
		const lane = `${run.repository.toLowerCase()}|${run.workflowId}|${run.branch}`;
		const latest = !seen.has(lane);
		seen.add(lane);
		if (run.state === 'running' || run.state === 'queued') {
			if (filter !== 'failed') groups.running.push(run);
		} else if (run.state === 'failed' && latest) {
			if (filter !== 'running') groups.failed.push(run);
		} else if (filter === 'all' || filter === 'releases') groups.recent.push(run);
	}
	groups.running.sort((a, b) => (a.state === b.state ? 0 : a.state === 'running' ? -1 : 1));
	return groups;
}

/** Running and waiting runs: the switch's count. */
export const runningCount = (runs: ReadonlyArray<AgentRun>) =>
	runs.filter((run) => run.state === 'running' || run.state === 'queued').length;

class AgentRunsStore {
	list: AgentRuns | null = null;
	failed = false;
	filter: RunFilter = 'all';
	repository: string | null = null;
	readonly details = observable.map<string, AgentRunDetail>();
	/** Runs whose last detail read failed (no access, gone, or GitHub down); a good read clears it. */
	readonly unreadable = observable.set<string>();
	private lastAt = 0;
	private repos: ReadonlyArray<string> = [];

	constructor() {
		makeAutoObservable<this, 'lastAt' | 'repos'>(this, {lastAt: false, repos: false}, {autoBind: true});
	}

	resetForTesting(): void {
		this.list = null;
		this.failed = false;
		this.filter = 'all';
		this.repository = null;
		this.details.clear();
		this.unreadable.clear();
		this.lastAt = 0;
		this.repos = [];
	}

	setFilter(filter: RunFilter): void {
		this.filter = filter;
	}

	setRepository(repository: string | null): void {
		this.repository = repository;
	}

	/** Reads the list (the API answers polls within 15 s from memory); `fresh` asks GitHub now. */
	async load(repos: ReadonlyArray<string>, opts: {fresh?: boolean; minAgeMs?: number} = {}): Promise<void> {
		const now = Date.now();
		if (!opts.fresh && now - this.lastAt < (opts.minAgeMs ?? 5_000)) return;
		this.lastAt = now;
		this.repos = repos;
		try {
			const response = await http.get<AgentRuns>(Endpoints.KONVO_AGENTS_RUNS, {
				query: {...(repos.length ? {repos: repos.slice(0, 20).join(',')} : {}), ...(opts.fresh ? {fresh: '1'} : {})},
				mode: 'silent',
			});
			runInAction(() => {
				if (!response.ok) {
					this.failed = true;
					return;
				}
				this.failed = false;
				this.list = response.body;
			});
		} catch {
			runInAction(() => {
				this.failed = true;
			});
		}
	}

	/** Something changed in the person's runs (the API's watcher saw it): read the list and open runs again. */
	refresh(): void {
		if (!this.list) return;
		void this.load(this.repos, {fresh: true});
		for (const detail of this.details.values())
			if (detail.run && (detail.run.state === 'running' || detail.run.state === 'queued'))
				void this.loadDetail({repository: detail.run.repository, runId: detail.run.id}).catch(() => undefined);
	}

	run(ref: AgentRunRef | null | undefined): AgentRun | null {
		if (!ref) return null;
		const detail = this.details.get(runKey(ref));
		if (detail?.run) return detail.run;
		return this.list?.runs.find((run) => runKey({repository: run.repository, runId: run.id}) === runKey(ref)) ?? null;
	}

	async loadDetail(ref: AgentRunRef): Promise<void> {
		const key = runKey(ref);
		try {
			const response = await http.post<AgentRunDetail>(Endpoints.KONVO_AGENTS_RUNS_DETAIL, {
				body: {repository: ref.repository, runId: ref.runId},
			});
			runInAction(() => {
				this.details.set(key, response.body);
				this.unreadable.delete(key);
			});
		} catch (error) {
			runInAction(() => this.unreadable.add(key));
			throw error;
		}
	}

	isUnreadable(ref: AgentRunRef): boolean {
		return this.unreadable.has(runKey(ref));
	}

	/** Re-run, re-run the failed jobs or cancel, as the person on GitHub. Throws the API error. */
	async act(ref: AgentRunRef, action: 'rerun' | 'rerun_failed' | 'cancel'): Promise<void> {
		await http.post(Endpoints.KONVO_AGENTS_RUNS_ACT, {body: {repository: ref.repository, runId: ref.runId, action}});
		void this.loadDetail(ref).catch(() => undefined);
		void this.load(this.repos, {fresh: true});
	}
}

const store = new AgentRunsStore();
export default store;
